import { useEffect, forwardRef, useImperativeHandle, useRef, useState, useContext } from "react";
import { fabric } from 'fabric';
import { AIEditContext } from "../../AIEditContext";
import useMultiInteractive from './hooks/useMultiInteractive'
import usePaint from './hooks/usePaint'

const formartNumber = (number, digits = 2) => {
    return Number(number.toFixed(digits))
}

const MagicEraseCanvas = forwardRef((props, ref) => {
    useImperativeHandle(ref, () => ({
        clear,
        getDataURL,
        // getSaveData,
        undo,
        redo,
        // loadSaveData,
    }));

    const patternImageBase64 = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAABHNCSVQICAgIfAhkiAAAADBJREFUOE9jfPP69X8GPEBYRASfNAPjqAHDIgz+AwG+iH7z5s1ZfPKMowYwDIMwAACuR1xZvSaHFwAAAABJRU5ErkJggg==';

    const { multiMasks, drawType, setUndoList, setRedoList, setZoom, zoom } = useContext(AIEditContext)

    const drawTypeRef = useRef()

    const bgCanvasRef = useRef()
    const bgInstanceRef = useRef()

    const topCanvasRef = useRef()
    const fabricTopCanvasRef = useRef()

    const canvasRef = useRef()
    const fabricInstanceRef = useRef()
    const [fabricInstance, setFabricInstance] = useState()

    const pencilBrushRef = useRef()
    const circleMouseRef = useRef()

    const modeRef = useRef()
    const moveableRef = useRef(props.moveable)
    const ctrlPressedRef = useRef(false)
    const displayScaleRef = useRef(props.displayScale)

    const [blueMaskBase64, setBlueMaskBase64] = useState()


    useEffect(() => {
        ctrlPressedRef.current = props.ctrlKeyPressed
    }, [props.ctrlKeyPressed])

    useEffect(() => {
        displayScaleRef.current = props.displayScale
    }, [props.displayScale])

    useEffect(() => {
        moveableRef.current = props.moveable
    }, [props.moveable])

    const isUndoableType = (item) => {
        // return ['path', 'rect', 'circle'].includes(item.type);
        return ['path', 'image', 'circle'].includes(item.type);
    };

    const getDataURL = () => {
        // const pathList = fabricInstanceRef.current.getObjects().filter(item => isUndoableType(item))
        // const tempCanvas = new fabric.StaticCanvas(null, {
        //     width: fabricInstanceRef.current.width,
        //     height: fabricInstanceRef.current.height,
        //     backgroundColor: '#000'
        // })
        // tempCanvas.add(...pathList)
        // const exportPath = tempCanvas.toDataURL({ format: 'png', multiplier: 1 / displayScaleRef.current })
        const objects = fabricInstanceRef.current.getObjects()
        const tempCanvas = new fabric.StaticCanvas(null, {
            width: fabricInstanceRef.current.width,
            height: fabricInstanceRef.current.height,
        })
        tempCanvas.add(...objects)
        const blueRes = tempCanvas.toDataURL({ format: 'png', multiplier: 1 / displayScaleRef.current });
        return blueRes
    }

    const clear = () => {
        // console.log('clear')
        pencilBrushRef.current._reset() // 清除path
        const pathList = fabricInstanceRef.current.getObjects().filter(item => isUndoableType(item))
        fabricInstanceRef.current.remove(...pathList)
        setBlueMaskBase64()
    }

    useEffect(() => {
        // 马赛克
        const pattern = new fabric.Pattern({
            source: patternImageBase64,
            repeat: 'repeat',
        })
        // 初始化canvas对象
        fabric.Image.fromURL(props.url, async (img) => {
            img.scale(props.displayScale) // 背景展示完全
            // 背景canvas
            const bgInstance = new fabric.Canvas(bgCanvasRef.current, {
                width: props.displayWidth,
                height: props.displayHeight,
                backgroundColor: pattern, // 加载马赛克背景
                backgroundImage: img, // 加载默认背景
                fireRightClick: false, // 启用右键
                stopContextMenu: true, // 禁用右键菜单
                isDrawingMode: false, // 禁用绘图模式
                // selection: false, // 禁用选中对象
            })
            // await addWatermark(bgInstance) // 添加水印
            bgInstanceRef.current = bgInstance

            // 绘图canvas
            const instance = new fabric.Canvas(canvasRef.current, {
                width: props.displayWidth,
                height: props.displayHeight,
                fireRightClick: false, // 启用右键
                stopContextMenu: true, // 禁用右键菜单
                isDrawingMode: true, // 禁用绘图模式
                // selection: false, // 禁用选中对象
            })
            instance.includeDefaultValues = false

            fabricInstanceRef.current = instance
            setFabricInstance(instance)

            // 顶部canvas
            const topInstance = new fabric.Canvas(topCanvasRef.current, {
                width: props.displayWidth,
                height: props.displayHeight,
                fireRightClick: false, // 启用右键
                stopContextMenu: true, // 禁用右键菜单
                renderOnAddRemove: true,
                isDrawingMode: false, // 禁用绘图模式
                selection: false, // 禁用选中对象
            })
            topInstance.includeDefaultValues = false
            fabricTopCanvasRef.current = topInstance
            if (instance) { // 保存一次空画布状态
                saveToUndoList()
            }

            props.setFabricInstance(bgInstance, instance, topInstance)
            
        }, { crossOrigin: 'anonymous' })

        return () => {
            if (bgInstanceRef.current) bgInstanceRef.current.dispose()
            if (fabricInstanceRef.current) fabricInstanceRef.current.dispose()
            if (fabricTopCanvasRef.current) fabricTopCanvasRef.current.dispose()
        }
    }, [])

    useEffect(() => {
        if (!bgInstanceRef.current) return
        const bgImage = bgInstanceRef.current.get('backgroundImage')
        bgImage.setSrc(props.url, () => bgInstanceRef.current.renderAll(), { crossOrigin: 'anonymous' })
    }, [props.url])

    useEffect(() => {
        if (!pencilBrushRef.current) return
        if (!fabricInstanceRef.current) return
        pencilBrushRef.current.width = props.size // props.size * props.displayScale
        circleMouseRef.current.set({ radius: props.size / 2 }) // props.size * props.displayScale / 2
        fabricInstanceRef.current.renderAll()
    }, [props.size, props.displayScale])

    useEffect(() => { // 控制是否启用绘图模式
        if (!fabricInstanceRef.current) return
        if (props.moveable || drawTypeRef.current != 'draw') {
            fabricInstanceRef.current.isDrawingMode = false
        } else {
            fabricInstanceRef.current.isDrawingMode = true
        }
    }, [props.moveable, drawTypeRef.current])

    // 调整指针样式
    useEffect(() => {
        if (!fabricInstanceRef.current) return
        drawTypeRef.current = drawType
        if (fabricInstanceRef.current) {
            if (drawTypeRef.current === 'draw') {
                fabricInstanceRef.current.isDrawingMode = true
            }
        }
    }, [drawType, fabricInstanceRef.current]);

    useEffect(() => {
        if (!fabricInstanceRef.current) return

        const canvasScaleX = props.displayWidth / fabricInstanceRef.current.width
        const canvasScaleY = props.displayHeight / fabricInstanceRef.current.height

        const bgImage = bgInstanceRef.current.get('backgroundImage')
        if (bgImage) bgImage.scale(props.displayScale)

        bgInstanceRef.current.viewportTransform[4] = bgInstanceRef.current.viewportTransform[4] * canvasScaleX
        bgInstanceRef.current.viewportTransform[5] = bgInstanceRef.current.viewportTransform[5] * canvasScaleY
        bgInstanceRef.current.setDimensions({ width: props.displayWidth, height: props.displayHeight })

        const objs1 = bgInstanceRef.current.getObjects()
        objs1.forEach(item => {
            item.set({
                scaleX: item.scaleX * canvasScaleX,
                scaleY: item.scaleY * canvasScaleY
            })
            item.setCoords()
        })

        fabricInstanceRef.current.viewportTransform[4] = fabricInstanceRef.current.viewportTransform[4] * canvasScaleX
        fabricInstanceRef.current.viewportTransform[5] = fabricInstanceRef.current.viewportTransform[5] * canvasScaleY
        fabricInstanceRef.current.setDimensions({ width: props.displayWidth, height: props.displayHeight })

        const objs = fabricInstanceRef.current.getObjects()
        objs.forEach(item => {
            if (item.type == 'path') {
                item.scale(item.scaleX * canvasScaleX)
                item.set({
                    left: formartNumber(item.left * canvasScaleX),
                    top: formartNumber(item.top * canvasScaleY),
                })
                item.setCoords()
            } else if (item.type == 'image') {
                item.set({
                    scaleX: item.scaleX * canvasScaleX,
                    scaleY: item.scaleY * canvasScaleY
                })
                item.setCoords()
            } else {
                item.set({
                    width: formartNumber(item.width * canvasScaleX),
                    height: formartNumber(item.height * canvasScaleX),
                    left: formartNumber(item.left * canvasScaleX),
                    top: formartNumber(item.top * canvasScaleY),
                })
                item.setCoords()
            }
        })

        fabricTopCanvasRef.current.viewportTransform[4] = fabricInstanceRef.current.viewportTransform[4] * canvasScaleX
        fabricTopCanvasRef.current.viewportTransform[5] = fabricInstanceRef.current.viewportTransform[5] * canvasScaleY
        fabricTopCanvasRef.current.setDimensions({ width: props.displayWidth, height: props.displayHeight })
        const objs2 = fabricTopCanvasRef.current.getObjects()
        objs2.forEach(item => {
            item.set({
                scaleX: item.scaleX * canvasScaleX,
                scaleY: item.scaleY * canvasScaleY
            })
            item.setCoords()
        })

        bgInstanceRef.current.renderAll()
        fabricInstanceRef.current.renderAll()
        fabricTopCanvasRef.current.renderAll()
    }, [props.displayWidth, props.displayHeight, props.displayScale])


    // 撤销及恢复----------------------------------------start
    const zoomRef = useRef()
    useEffect(() => {
        zoomRef.current = zoom
    }, [zoom])

    const saveToUndoList = () => {
        if (!fabricInstanceRef.current || !bgInstanceRef.current || !fabricTopCanvasRef.current) return;

        const currentState = {
            mainCanvas: {
                json: fabricInstanceRef.current.toJSON(),
                viewportTransform: fabricInstanceRef.current.viewportTransform.slice(),
            },
            bgCanvas: {
                viewportTransform: bgInstanceRef.current.viewportTransform.slice(),
            },
            topCanvas: {
                viewportTransform: fabricTopCanvasRef.current.viewportTransform.slice(),
            },
            zoomValue: zoomRef.current
        };

        setUndoList(prevUndoList => [...prevUndoList, currentState]);
        setRedoList([]);
    };

    const applyCanvasState = (state) => {
        const { mainCanvas, bgCanvas, topCanvas, zoomValue } = state;

        fabricInstanceRef.current.loadFromJSON(mainCanvas.json, () => {
            fabricInstanceRef.current.viewportTransform = mainCanvas.viewportTransform;
            console.log('mainCanvas.viewportTransform', mainCanvas.viewportTransform)
            fabricInstanceRef.current.getObjects().forEach(item => {
                if (item.type == 'image') {
                    item.scale(displayScaleRef.current);
                }
            });
            bgInstanceRef.current.viewportTransform = [1, 0, 0, 1, 0, 0]
            bgInstanceRef.current.viewportTransform = bgCanvas.viewportTransform;
            console.log('bgCanvas.viewportTransform', bgCanvas.viewportTransform)

            fabricTopCanvasRef.current.viewportTransform = [1, 0, 0, 1, 0, 0]
            fabricTopCanvasRef.current.viewportTransform = topCanvas.viewportTransform;

            setZoom(zoomValue)
            bgInstanceRef.current.renderAll();
            fabricInstanceRef.current.renderAll();
            fabricTopCanvasRef.current.renderAll();
        });
    };

    const undo = () => {
        setUndoList(prevUndoList => {
            if (prevUndoList.length > 1) {
                const newUndoList = [...prevUndoList];
                const lastState = newUndoList.pop();
                setRedoList(prevRedoList => [lastState, ...prevRedoList]);

                const previousState = newUndoList[newUndoList.length - 1];
                applyCanvasState(previousState);

                return newUndoList;
            }
            return prevUndoList;
        });
    };

    const redo = () => {
        setRedoList(prevRedoList => {
            if (prevRedoList.length > 0) {
                const newRedoList = [...prevRedoList];
                const nextState = newRedoList.shift();
                setUndoList(prevUndoList => [...prevUndoList, nextState]);

                applyCanvasState(nextState);

                return newRedoList;
            }
            return prevRedoList;
        });
    };
    // 撤销及恢复----------------------------------------end


    // 多语义分割----------------------------------------start

    useMultiInteractive({
        instance: fabricInstance,
        topInstance: fabricTopCanvasRef.current,
        multiMasks: multiMasks,
        setBlueMaskBase64: setBlueMaskBase64,
        displayScale: props.displayScale,
        operationType: drawType == 'multi_add' ? 'add' : 'remove'
    })
    // 多语义选择了mask后绘制到中间层画布
    useEffect(() => {
        if (!fabricInstance || !blueMaskBase64) return
        const objs = fabricInstance.getObjects()
        const img = objs.find(item => item.get('is_mask'))
        if (img) fabricInstance.remove(img)
        fabric.Image.fromURL(blueMaskBase64, (img) => {
            img.scale(displayScaleRef.current)
            img.set({
                hoverCursor: 'pointer',
                hasBorders: false,
                selectable: false,
                globalCompositeOperation: 'source-over',
            })
            img.set('is_mask', true)
            img.set('unclearable', true)
            fabricInstance.add(img)
            fabricInstance.renderAll()
            // const imageList = fabricInstance.getObjects().filter(item => item.type == 'image')
            saveToUndoList() // 每次绘制保存一下将要撤销的json快照
        })
    }, [blueMaskBase64, fabricInstance])
    // 多语义分割----------------------------------------end

    usePaint(fabricInstanceRef, pencilBrushRef, circleMouseRef, drawTypeRef, moveableRef, ctrlPressedRef, props, saveToUndoList);

    return (
        <>
            <div className="replace_canvasBox">
                <div className="replace_bgcanvas"><canvas ref={bgCanvasRef} /></div>
                <div className="replace_canvas"><canvas ref={canvasRef} /></div>
                <div className={drawType !== 'draw' ? 'replace_topcanvas' : 'replace_topcanvas_hidden'}><canvas ref={topCanvasRef} /></div>
            </div>

        </>

    )
})

export default MagicEraseCanvas